import { useState } from "react";

import classes from "./VitaminInjections.module.css";
import { treatmentPages, servicesData } from "./serviceData";
import BookingWidgetSection from "../../Components/BookingWidget/BookingWidgetSection";
import useDocumentMeta from "../../utils/useDocumentMeta";
import useSectionView from "../../utils/useSectionView";
import {
  BOOKING_URL,
  trackBookingClick,
  trackFaqToggle,
} from "../../utils/tracking";

import heroImage from "../../Assets/ra_candles.jpg";

const page = treatmentPages.find((p) => p.slug === "vitamin-injections");
const services = servicesData.vitaminInjections;
const findService = (id) => services.find((s) => s.id === id);

const treatmentCards = [
  {
    serviceId: "vitamin-b12-injection",
    highlights: [
      "Supports energy, mood and concentration",
      "Supports cognitive and cardiovascular health",
      "Helps address B12 deficiency symptoms",
      "Quick and easy, with no downtime",
    ],
  },
  {
    serviceId: "biotin-injection",
    highlights: [
      "Supports stronger, healthier-looking hair",
      "Supports skin health and condition",
      "Supports stronger nails",
      "Quick and easy, with no downtime",
    ],
  },
];

const faqs = [
  {
    q: "What's the difference between B12 and Biotin injections?",
    a: "Vitamin B12 supports energy, mood, concentration and overall wellbeing, and can help address symptoms of B12 deficiency. Biotin (Vitamin B7) is best known for supporting hair, skin and nail health. We can advise which is right for you at your consultation.",
  },
  {
    q: "How often should I have this treatment?",
    a: "This varies by individual and is best discussed at your consultation, based on your goals and any deficiency symptoms you may be experiencing.",
  },
  {
    q: "Is the injection painful?",
    a: "It's a quick intramuscular injection — most clients find it very quick and easy, with only brief, mild discomfort at the injection site.",
  },
  {
    q: "Can I combine this with another treatment on the same day?",
    a: "Yes — vitamin injections are quick to fit alongside a facial or other treatment during the same appointment. Let us know when you book.",
  },
];

const VitaminInjectionsPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const faqSectionRef = useSectionView("Vitamin Injections FAQ");

  useDocumentMeta(
    `${page.title} | Radiance Aesthetics | Hindley | Wigan`,
    page.metaDescription,
  );

  return (
    <>
      {/* Hero */}
      <section className={classes.heroSection}>
        <div className={classes.heroContainer}>
          <div className={classes.heroContent}>
            <p className={classes.heroSubtitle}>
              Nurse-Led · Quick Wellness Boost
            </p>
            <h1 className={classes.heroTitle}>Vitamin Injections</h1>
            <p className={classes.heroMeta}>
              <span>£25</span> · 15 mins
            </p>
            <p className={classes.heroDescription}>
              Quick, effective B12 and Biotin injections to support your energy,
              wellbeing, hair, skin and nails — in and out in 15 minutes, with
              no downtime.
            </p>
            <a
              href={BOOKING_URL}
              className={classes.heroCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Vitamin Injections Hero")}
            >
              Book Now
            </a>
          </div>
          <div className={classes.heroImageWrapper}>
            <img
              className={classes.heroImage}
              src={heroImage}
              alt="Calming treatment environment at Radiance Aesthetics Wigan"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Highlight banner */}
      <section className={classes.section}>
        <div className={classes.highlightBannerWrapper}>
          <div className={classes.highlightBanner}>
            <h2 className={classes.highlightTitle}>
              Quick, Effective Wellness Support
            </h2>
            <p className={classes.highlightText}>
              No elaborate ritual needed — just a{" "}
              <strong>fast, simple intramuscular injection</strong>.
              Consultation and treatment take{" "}
              <strong>no more than 15 minutes</strong>, with no downtime, so
              it's easy to fit into your day, on its own or alongside another
              treatment.
            </p>
          </div>
        </div>
      </section>

      {/* Treatment cards */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Choose Your Treatment</p>
          <h2 className={classes.sectionTitle}>B12 or Biotin?</h2>
          <p className={classes.sectionDescription}>
            Both treatments are £25 for a 15-minute appointment, including
            consultation.
          </p>
          <div className={classes.treatmentGrid}>
            {treatmentCards.map((card) => {
              const service = findService(card.serviceId);
              if (!service) return null;
              return (
                <div className={classes.treatmentCard} key={card.serviceId}>
                  <h3 className={classes.treatmentName}>{service.name}</h3>
                  <p className={classes.treatmentPrice}>£{service.price}</p>
                  <p className={classes.treatmentDuration}>
                    {service.duration}
                  </p>
                  <ul className={classes.treatmentHighlights}>
                    {card.highlights.map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                  <a
                    href={BOOKING_URL}
                    className={classes.treatmentButton}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackBookingClick(`${service.name} Card`)}
                  >
                    Book This Treatment
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={classes.section} ref={faqSectionRef}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>FAQ</p>
          <h2 className={classes.sectionTitle}>Common Questions</h2>
          <div className={classes.faqContainer}>
            {faqs.map((faq, i) => (
              <div className={classes.faqItem} key={faq.q}>
                <h3
                  className={classes.faqQuestion}
                  onClick={() => {
                    const opening = expandedFaq !== i;
                    trackFaqToggle(faq.q, i, opening);
                    setExpandedFaq(opening ? i : null);
                  }}
                  role="button"
                  aria-expanded={expandedFaq === i}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const opening = expandedFaq !== i;
                      trackFaqToggle(faq.q, i, opening);
                      setExpandedFaq(opening ? i : null);
                    }
                  }}
                >
                  {faq.q} {expandedFaq === i ? "−" : "+"}
                </h3>
                {expandedFaq === i && (
                  <p className={classes.faqAnswer}>{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={classes.ctaSection} aria-label="Book appointment">
        <h2 className={classes.ctaTitle}>Ready for a Quick Wellness Boost?</h2>
        <p className={classes.ctaDescription}>
          Book online today, or get in touch for a free consultation to find the
          right treatment for you.
        </p>
        <a
          href={BOOKING_URL}
          className={classes.ctaButton}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackBookingClick("Vitamin Injections Bottom CTA")}
        >
          Book Now
        </a>
      </section>

      <div className={classes.ctaWidgetWrapper}>
        <BookingWidgetSection source="Treatments - Vitamin Injections" />
      </div>
    </>
  );
};

export default VitaminInjectionsPage;
